import { Subscribable, Subscription } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { ChartConfigRequest, ChartConfigRestfulEvent } from './request';
import { ChartExtendedContent, ChartExtendedMetaInfo } from './entities';
import { ChartConfigStore, ChartConfigStoreEvent } from './store';
import { oneRequestAtATime } from '@benzinga/utils';
import { SafePromise, SafeType, SafeError } from '@benzinga/safe-await';

interface ChartDeletedEvent {
  type: 'chart-config:chart-deleted';
  uuid: string | null;
}

export type ChartConfigManagerEvent = ChartConfigRestfulEvent | ChartConfigStoreEvent | ChartDeletedEvent;

export class ChartConfigManager extends Subscribable<ChartConfigManagerEvent> {
  private request: ChartConfigRequest;
  private store: ChartConfigStore;
  private requestSubscription?: Subscription<ChartConfigRequest>;
  private storeSubscription?: Subscription<ChartConfigStore>;

  private fetchCharts = oneRequestAtATime(async (): SafePromise<ChartExtendedMetaInfo[]> => {
    const response = await this.request.getCharts();
    if (response.err) {
      return response as SafeType<ChartExtendedMetaInfo[]>;
    } else {
      this.store.setCharts(response.ok);
      return response;
    }
  });

  constructor(session: Session) {
    super();
    this.request = new ChartConfigRequest(session);
    this.store = new ChartConfigStore();
  }

  public static getName = () => 'benzinga-chart-config';

  public getChartData = async (guid: string) => {
    if (!guid) {
      return { err: new SafeError('Guid is required', 'chart-config:get_chart_data') };
    }
    const chart = await this.request.getChartData(guid);
    if (chart.ok) {
      if ((chart.ok?.version ?? 0) > 1) {
        this.store.setCachedChart(guid, { ...chart.ok, guid });
      } else {
        if (chart.ok?.content) {
          const data = JSON.parse((chart.ok?.content as unknown as string) ?? '{}');
          this.store.setCachedChart(guid, {
            ...data,
            content: JSON.parse(data.content),
            guid,
            version: 2,
          });
        }
      }

      const chartConfig = this.store.getCachedChart(guid);
      if (chartConfig) return { ok: chartConfig };
    }
    return chart;
  };

  public getCachedChartData = (guid: string) => this.store.getCachedChart(guid);

  public updateChartData = async (
    chartGuid: string,
    chartState: Omit<ChartExtendedContent, 'version'> & Required<Pick<ChartExtendedContent, 'version'>>,
  ) => {
    this.store.setCachedChart(chartGuid, chartState);
    const response = await this.request.putChartData(chartGuid, chartState);
    this.dispatch({
      chart: chartState,
      type: 'chart-config:post-chart-updated',
      uuid: chartGuid,
    });
    return response;
  };

  public newChartData = async (
    chart: Omit<ChartExtendedContent, 'version' | 'guid'> & Required<Pick<ChartExtendedContent, 'version'>>,
  ) => {
    const response = await this.request.postChartData(chart);
    if (response.ok) {
      this.store.setCachedChart(response.ok.guid, { ...chart, guid: response.ok.guid });
    }
    return response;
  };

  public deleteChartData = (guid: string) => {
    this.dispatch({
      type: 'chart-config:chart-deleted',
      uuid: guid ?? null,
    });
    return this.request.deleteChartData(guid).then(resp => {
      if (resp.ok) {
        this.store.setDrawingCharts(this.store.getCachedDrawingCharts()?.filter(chart => chart.guid !== guid) ?? []);
        this.store.setCharts(this.store.getCharts()?.filter(chart => chart.guid !== guid) ?? []);
        this.store.setSavedCharts(this.store.getCachedSavedCharts()?.filter(chart => chart.guid !== guid) ?? []);
      }
      return resp;
    });
  };

  public getCharts = async (force?: boolean): SafePromise<ChartExtendedMetaInfo[]> => {
    if (force || this.store.shouldWeFetchCharts() === true) {
      return await this.fetchCharts();
    } else {
      return { ok: this.store.getCharts() ?? [] };
    }
  };

  public getVisibleCharts = async (force?: boolean): SafePromise<ChartExtendedMetaInfo[]> => {
    await this.getCharts(force);
    return { ok: this.getCachedVisibleCharts() ?? [] };
  };

  public getSavedCharts = async (force?: boolean): SafePromise<ChartExtendedContent[]> => {
    const charts = await this.getVisibleCharts(force);

    if (charts.err) {
      return { err: charts.err };
    } else {
      const chartsData = charts.ok.map(async c => {
        const data = await this.getChartData(c.guid);
        if (data.ok && !data.ok?.drawing) {
          return data.ok;
        } else {
          return undefined;
        }
      });

      const savedChart = (await Promise.all(chartsData)).filter(
        (a: ChartExtendedContent | undefined): a is ChartExtendedContent => !!a,
      );

      this.store.setSavedCharts(savedChart);
      if (savedChart) {
        return { ok: savedChart };
      } else {
        return { ok: [] };
      }
    }
  };

  public getCachedSavedCharts = () => this.store.getCachedSavedCharts();

  public getDrawingCharts = async (force?: boolean): SafePromise<ChartExtendedContent[]> => {
    const charts = await this.getVisibleCharts(force);

    if (charts.err) {
      return { err: charts.err };
    } else {
      const chartsData = charts.ok.map(async c => {
        const data = await this.getChartData(c.guid);
        if (data.ok?.drawing) {
          return data.ok;
        } else {
          return undefined;
        }
      });

      const drawingCharts = (await Promise.all(chartsData)).filter(
        (a: ChartExtendedContent | undefined): a is ChartExtendedContent => !!a,
      );

      this.store.setDrawingCharts(drawingCharts);
      if (drawingCharts) {
        return { ok: drawingCharts };
      } else {
        return { ok: [] };
      }
    }
  };

  public getCachedDrawingCharts = () => this.store.getCachedDrawingCharts();

  public getCachedHiddenCharts = (): ChartExtendedMetaInfo[] | undefined => this.store.getCachedHiddenCharts();
  public getCachedVisibleCharts = (): ChartExtendedMetaInfo[] | undefined => this.store.getCachedVisibleCharts();

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
    this.storeSubscription = this.store.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  }
}
